import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { TranslateContext } from "../../context/TranslateContext";
import { useCustomTheme } from "../../utils/useCustomTheme";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import Content from "./Content";
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  ColorLens as ColorLensIcon,
  Paid,
  PermMedia,
  SupportAgent,
  PriceCheck,
  AutoFixHigh,
  AutoAwesome,
  Unsubscribe,
  ContactMail,
  AddCard,
  ContentPaste,
  Language,
  Translate,
  BarChart as BarChartIcon,
  TuneOutlined,
  SmartToyOutlined,
  StoreOutlined,
  PaletteOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";

const drawerWidth = 260;

const AdminDashboard = () => {
  const { lang } = React.useContext(TranslateContext);
  const [web, setWeb] = React.useState({});
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const { toggleColorMode, isDark } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  async function handleGetWeb() {
    const res = await hitAxios({
      path: "/api/web/get_web_public",
      post: false,
      admin: false,
    });
    if (res.data.success) setWeb(res.data.data);
  }

  // ── MENU GROUPS ────────────────────────────────────────────────────────────
  const menuItems = [
    // ── 1. Overview ──────────────────────────────────────────────────────────
    {
      id: "dashboards",
      text: lang?.dashboards || "Dashboard",
      icon: <DashboardIcon />,
    },

    // ── 2. Users ─────────────────────────────────────────────────────────────
    {
      id: "users",
      text: lang?.users || "Users",
      icon: <GroupIcon />,
    },

    // ── 3. AI & Automation ───────────────────────────────────────────────────
    {
      id: "ai-automation",
      text: lang?.aiAutomation || "AI & Automation",
      icon: <SmartToyOutlined />,
      submenu: [
        {
          id: "ai-provider",
          text: lang?.aiProvider || "AI Providers",
        },
        {
          id: "prompt-template",
          text: lang?.promptTe || "Prompt Templates",
        },
        {
          id: "templates",
          text: lang?.templates || "Templates",
        },
        {
          id: "task-pricing",
          text: lang?.taskPricing || "Task Pricing",
        },
      ],
    },

    // ── 4. Monetization ──────────────────────────────────────────────────────
    {
      id: "monetization",
      text: lang?.monetization || "Monetization",
      icon: <StoreOutlined />,
      submenu: [
        {
          id: "plans",
          text: lang?.plans || "Plans",
        },
        {
          id: "payment-gateway",
          text: lang?.paymentGateway || "Payment Gateways",
        },
        {
          id: "orders",
          text: lang?.orders || "Orders",
        },
      ],
    },

    // ── 5. Appearance ─────────────────────────────────────────────────────────
    {
      id: "appearance",
      text: lang?.appearance || "Appearance",
      icon: <PaletteOutlined />,
      submenu: [
        {
          id: "web-theme",
          text: lang?.webTheme || "Web Theme",
        },
        {
          id: "site-settings",
          text: lang?.siteSettings || "Site Settings",
        },
        {
          id: "social-login",
          text: lang?.socialLogin || "Social Login",
        },
      ],
    },

    // ── 6. Content ────────────────────────────────────────────────────────────
    {
      id: "content",
      text: lang?.content || "Content",
      icon: <ContentPaste />,
      submenu: [
        {
          id: "blogs",
          text: lang?.blogs || "Blogs",
        },
        {
          id: "leads",
          text: lang?.leads || "Leads",
        },
      ],
    },

    // ── 7. Communications ─────────────────────────────────────────────────────
    {
      id: "communications",
      text: lang?.communications || "Communications",
      icon: <Unsubscribe />,
      submenu: [
        {
          id: "email-settings",
          text: lang?.emailSettings || "Email Settings",
        },
        {
          id: "email-templates",
          text: lang?.emailTemplates || "Email Templates",
        },
      ],
    },

    // ── 8. Localization ───────────────────────────────────────────────────────
    {
      id: "localization",
      text: lang?.localization || "Localization",
      icon: <Translate />,
      submenu: [
        {
          id: "web-translation",
          text: lang?.webTranslation || "Web Translation",
        },
      ],
    },

    // ── 9. Support ────────────────────────────────────────────────────────────
    {
      id: "help",
      text: lang?.help || "Help & Support",
      icon: <SupportAgent />,
    },
  ];

  // ── URL sync helpers ───────────────────────────────────────────────────────
  const getInitialMenu = () => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    if (pageParam) {
      const isValid = menuItems.some(
        (item) =>
          item.id === pageParam ||
          item.submenu?.some((sub) => sub.id === pageParam),
      );
      if (isValid) return pageParam;
    }
    return menuItems[0]?.submenu?.[0]?.id || menuItems[0]?.id || "dashboards";
  };

  const [selectedMenu, setSelectedMenu] = React.useState(getInitialMenu);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openSubmenus, setOpenSubmenus] = React.useState(() => {
    const initialMenu = getInitialMenu();
    const openMenus = {};
    menuItems.forEach((item) => {
      if (item.submenu?.some((sub) => sub.id === initialMenu)) {
        openMenus[item.id] = true;
      }
    });
    return openMenus;
  });

  const updateURL = (menuId) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", menuId);
    window.history.pushState(
      { path: `${window.location.pathname}?${params}` },
      "",
      `${window.location.pathname}?${params}`,
    );
  };

  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  const handleMenuClick = (menuId, hasSubmenu) => {
    if (hasSubmenu) {
      setOpenSubmenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
    } else {
      setSelectedMenu(menuId);
      updateURL(menuId);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleSubmenuClick = (submenuId) => {
    setSelectedMenu(submenuId);
    updateURL(submenuId);
    if (isMobile) setMobileOpen(false);
  };

  React.useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get("page");
      if (pageParam) {
        setSelectedMenu(pageParam);
        menuItems.forEach((item) => {
          if (item.submenu?.some((sub) => sub.id === pageParam)) {
            setOpenSubmenus((prev) => ({ ...prev, [item.id]: true }));
          }
        });
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  React.useEffect(() => {
    handleGetWeb();
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Sidebar
        lang={lang}
        theme={theme}
        isMobile={isMobile}
        selectedMenu={selectedMenu}
        openSubmenus={openSubmenus}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        handleMenuClick={handleMenuClick}
        handleSubmenuClick={handleSubmenuClick}
        menuItems={menuItems}
        web={web}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, md: 0 },
        }}
      >
        <TopBar
          lang={lang}
          theme={theme}
          isMobile={isMobile}
          isDark={isDark}
          selectedMenu={selectedMenu}
          toggleColorMode={toggleColorMode}
          handleDrawerToggle={handleDrawerToggle}
          web={web}
        />

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Content selectedMenu={selectedMenu} web={web} />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
